// src/components/timeBoxes/TimeBoxDetail/TimeBoxDetailContent.tsx
// TimeBox 詳情內容區：標題、描述、AI推薦、關聯任務

import React, { useState, useRef, useCallback } from 'react';
import { motion } from 'framer-motion';
import { TimeBox, TimeBoxCategory } from '../../../../types/timeBox';
import { Task } from '../../../../types/task';
import { TimeRecommendation } from '../../../../types/scheduleRecommendation';
import EditableText from '../../../../components/common/EditableText';
import AIRecommendationsSection from '../AIRecommendationsSection';
import { X, CheckCircle, Circle } from '../../../../components/common/icons';
import { useTaskStatusToggle } from '../../../../hooks/useTaskStatusToggle';
import { useAuth } from '../../../../contexts/AuthContext';
import { categoryColors } from '../../../../types/task';
import { TaskSelectorInline } from '../TaskSelector/TaskSelectorInline';
import { logger } from '../../../../utils/logger';
import { timeBoxCategoryConfig } from '../../../../constants/timebox.constants';

interface TimeBoxDetailContentProps {
  timeBox: TimeBox;
  onUpdateTitle?: (title: string) => Promise<void>;
  onUpdateNotes?: (notes: string | undefined) => Promise<void>;
  onUpdateTaskIds?: (taskIds: string[]) => Promise<void>;
  onCategoryChange?: (category: TimeBoxCategory) => Promise<void>;

  // AI 推薦相關
  aiRecommendations?: TimeRecommendation[];
  isLoadingRecommendations?: boolean;
  recommendationsError?: string | null;
  onSelectRecommendation?: (recommendation: TimeRecommendation) => void;

  // 關聯任務資訊
  relatedTaskInfo?: {
    title: string;
    status: string;
  };
  relatedTasks?: Task[];
  isLoadingRelatedTasks?: boolean;
}

export const TimeBoxDetailContent: React.FC<TimeBoxDetailContentProps> = ({
  timeBox,
  onUpdateTitle,
  onUpdateNotes,
  onUpdateTaskIds,
  onCategoryChange,
  aiRecommendations = [],
  isLoadingRecommendations = false,
  recommendationsError = null,
  onSelectRecommendation,
  relatedTaskInfo,
  relatedTasks = [],
  isLoadingRelatedTasks = false,
}) => {
  const [localRelatedTasks, setLocalRelatedTasks] = useState<Task[]>(relatedTasks);

  // 取得當前用戶
  const { currentUser } = useAuth();
  const userId = currentUser?.uid || '';

  // 初始化任務狀態切換 Hook
  const { toggleTaskStatus } = useTaskStatusToggle(userId, {
    onBeforeToggle: (taskId, newStatus) => {
      // 樂觀更新本地 UI
      setLocalRelatedTasks(prev =>
        prev.map(t => t.id === taskId ? { ...t, status: newStatus } : t)
      );
    },
    onToggleError: (taskId, error) => {
      // 錯誤時回滾到原始狀態
      setLocalRelatedTasks(relatedTasks);
      logger.error('TaskStatus', 'Failed to toggle task status', error);
    }
  });

  // 同步 relatedTasks 到本地狀態
  React.useEffect(() => {
    setLocalRelatedTasks(relatedTasks);
  }, [relatedTasks]);

  // Category 循環切換邏輯（與 MetadataRow 的 inlineCycle 一致）
  const lastClickTimeRef = useRef<number>(0);
  const categoryOptions = Object.keys(timeBoxCategoryConfig) as TimeBoxCategory[];

  const handleCategoryClick = useCallback(() => {
    if (!onCategoryChange) return;

    const now = Date.now();
    lastClickTimeRef.current = now;

    const currentIndex = categoryOptions.indexOf(timeBox.category);
    const nextIndex = (currentIndex + 1) % categoryOptions.length;
    const nextCategory = categoryOptions[nextIndex];

    // 樂觀更新：立即呼叫（由上層處理 debounce）
    onCategoryChange(nextCategory);
  }, [onCategoryChange, timeBox.category, categoryOptions]);

  const handleRemoveTask = async (taskIdToRemove: string) => {
    if (!onUpdateTaskIds || !timeBox.taskIds) return;
    const newTaskIds = timeBox.taskIds.filter(id => id !== taskIdToRemove);
    await onUpdateTaskIds(newTaskIds);
  };

  const handleSelectTask = async (taskId: string) => {
    if (!onUpdateTaskIds) return;
    const currentTaskIds = timeBox.taskIds || [];
    if (currentTaskIds.includes(taskId)) {
      // 已存在，不重複添加
      return;
    }
    await onUpdateTaskIds([...currentTaskIds, taskId]);
  };

  // 獲取 Category 配置
  const categoryConfig = timeBoxCategoryConfig[timeBox.category];
  const CategoryIcon = categoryConfig.icon;

  return (
    <div className="flex-1 md:min-w-0 flex flex-col">
      {/* TimeBox Title */}
      <div className="mb-6 px-4">
        {onUpdateTitle ? (
          <EditableText
            value={timeBox.title}
            onSave={onUpdateTitle}
            placeholder="Enter time box title..."
            maxLength={100}
            required
            className="text-xl font-semibold text-gray-800"
            inputClassName="text-xl font-semibold text-gray-800"
          />
        ) : (
          <h1 className="text-xl font-semibold text-gray-800 break-words">
            {timeBox.title}
          </h1>
        )}

        {/* Category Badge - 可點擊循環切換 */}
        <div className="mt-2">
          <button
            onClick={handleCategoryClick}
            disabled={!onCategoryChange}
            className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${categoryConfig.color}
                       ${onCategoryChange ? 'cursor-pointer hover:opacity-80 active:scale-95 transition-all' : 'cursor-default'}`}
            title={onCategoryChange ? `Category: ${categoryConfig.label} (click to cycle)` : categoryConfig.label}
          >
            <CategoryIcon size={14} strokeWidth={2.5} />
            <span>{categoryConfig.label}</span>
          </button>
        </div>
      </div>

      {/* Related Task Section */}
      <section className="mb-6 px-4">
        {/* 舊有的單一 taskId 顯示 (Legacy support) */}
        {timeBox.taskId && relatedTaskInfo && !timeBox.taskIds?.includes(timeBox.taskId) && (
          <div className="mb-3 p-3 bg-sky-50 rounded-lg border border-sky-100 flex items-start justify-between">
            <div>
              <p className="text-sm font-medium text-gray-800">{relatedTaskInfo.title}</p>
              <div className="flex items-center mt-1">
                <span className={`inline-block w-2 h-2 rounded-full mr-1.5 ${relatedTaskInfo.status === 'CLOSED' ? 'bg-green-500' : 'bg-gray-400'}`}></span>
                <p className="text-xs text-gray-600 capitalize">{relatedTaskInfo.status.toLowerCase().replace('_', ' ')}</p>
              </div>
            </div>
          </div>
        )}

        {/* 多任務列表顯示 */}
        {isLoadingRelatedTasks ? (
          <div className="py-2 text-sm text-gray-500">Loading tasks...</div>
        ) : localRelatedTasks.length > 0 ? (
          <div className="border border-gray-200 rounded-lg overflow-hidden divide-y divide-gray-100">
            {localRelatedTasks.map((task) => (
              <motion.div
                key={task.id}
                className="group flex items-center justify-between px-3 py-2.5 hover:bg-gray-50 transition-colors bg-white"
                initial={false}
                animate={{
                  opacity: task.status === 'CLOSED' ? 0.6 : 1,
                }}
                transition={{ duration: 0.2 }}
              >
                <div className="flex items-center min-w-0 flex-1 mr-3">
                  {/* 狀態圓圈 - 可點擊切換 */}
                  <motion.button
                    onClick={() => toggleTaskStatus(task)}
                    className="shrink-0 mr-3 cursor-pointer focus:outline-none"
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.95 }}
                    aria-label={task.status === 'CLOSED' ? 'Mark as open' : 'Mark as closed'}
                  >
                    {task.status === 'CLOSED' ? (
                      <CheckCircle size={18} className="text-green-500" />
                    ) : (
                      <Circle size={18} className="text-gray-400" />
                    )}
                  </motion.button>

                  <div className="min-w-0 flex-1">
                    <p className={`text-sm font-medium text-gray-800 truncate ${task.status === 'CLOSED' ? 'line-through text-gray-500' : ''}`}>
                      {task.title}
                    </p>
                    {task.category && (
                      <span className={`inline-block px-2 py-0.5 text-xs rounded-full mt-1 ${categoryColors[task.category]}`}>
                        {task.category}
                      </span>
                    )}
                  </div>
                </div>
                {onUpdateTaskIds && (
                  <button
                    onClick={() => handleRemoveTask(task.id)}
                    className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-full transition-colors md:opacity-0 md:group-hover:opacity-100 md:focus:opacity-100"
                    title="Remove task"
                  >
                    <X size={16} />
                  </button>
                )}
              </motion.div>
            ))}
          </div>
        ) : null}

        {/* Inline 任務選擇器 */}
        {onUpdateTaskIds && (
          <div className={localRelatedTasks.length > 0 ? 'mt-3' : ''}>
            <TaskSelectorInline
              onSelectTask={handleSelectTask}
              excludeTaskIds={timeBox.taskIds || []}
              relatedTasks={localRelatedTasks}
              userId={userId}
            />
          </div>
        )}
      </section>

      {/* Notes */}
      <section className="mb-6 px-4">
        {onUpdateNotes ? (
          <EditableText
            value={timeBox.notes || ''}
            onSave={async (value) => {
              await onUpdateNotes(value || undefined);
            }}
            placeholder="Add notes..."
            maxLength={5000}
            multiline
            rows={4}
            minHeight="80px"
            className="text-gray-800"
            inputClassName="text-gray-800"
          />
        ) : (
          timeBox.notes && (
            <p className="text-gray-800 whitespace-pre-line">{timeBox.notes}</p>
          )
        )}
      </section>

      {/* AI Recommendations Section */}
      {(aiRecommendations.length > 0 || isLoadingRecommendations || recommendationsError) && onSelectRecommendation && (
        <section className="mb-6 px-4">
          <AIRecommendationsSection
            recommendations={aiRecommendations}
            isLoading={isLoadingRecommendations}
            error={recommendationsError}
            onSelectRecommendation={onSelectRecommendation}
            showErrorBanner={false}
          />
        </section>
      )}
    </div>
  );
};
